import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { CheckExistingOrder } from '../public/store/+state/stores.actions';
import {
  getCurrentCartState,
  getSelectedStore,
} from '../public/store/+state/stores.selectors';
import { ClientStore } from '../stores/stores';
import { InactivityService } from './inactivity.service';

/**
 * Envelope shapes the customer storefront accepts from the AI Ordering
 * Assistant widget (`stores-ai-ordering-frontend`) over `window.postMessage`.
 *
 * Kept here so the host owns the contract version on its side; the widget
 * defines a matching union in `src/bridge/postToHost.ts`.
 */
interface AiOrderDraftUpdatedMessage {
  type: 'AI_ORDER_DRAFT_UPDATED';
  storeId: number;
  orderId: string | null;
}

interface AiOrderUserActiveMessage {
  type: 'AI_ORDER_USER_ACTIVE';
  storeId: number;
  orderId: string | null;
  source: 'text' | 'voice';
}

type AiOrderEnvelope = AiOrderDraftUpdatedMessage | AiOrderUserActiveMessage;

/**
 * Bridge between the AI Ordering Assistant iframe widget and the customer-
 * storefront host (this Angular app).
 *
 * The widget is embedded as a sandboxed iframe served from `order-ai.<domain>`
 * and has no other channel to the host page; iframe DOM events do not bubble
 * out, and the widget's local Zustand state is not visible to the host. Two
 * cross-document concerns are addressed here:
 *
 *   1. **Basket reactivity** (card #9683) — when the AI adds, updates, or
 *      removes a draft order line via chat, the widget posts an
 *      `AI_ORDER_DRAFT_UPDATED` envelope. The host responds by dispatching
 *      the existing `CheckExistingOrder` action, which re-fetches the order
 *      from the GO backend (the AI ordering backend has already written the
 *      change there via its `api_url` round-trip) and refreshes the NgRx
 *      cart state. The basket then re-renders through the same path as a
 *      page reload, without simulating local cart actions that would
 *      otherwise hit the backend a second time and double-add the line.
 *
 *   2. **Inactivity timer reset** (card #9685) — every typed character,
 *      message send, and microphone event inside the widget posts an
 *      `AI_ORDER_USER_ACTIVE` envelope. The host treats it as user
 *      activity by delegating to `InactivityService.notifyExternalActivity`,
 *      which re-arms the countdown (pre-modal) or resets the auto-close
 *      timer (modal-up).
 *
 * The service is **per-store gated**: the `message` listener is attached
 * only while the currently-selected store has `settings.ORDERS_AI_ENABLED`
 * set to `true`. On tenants where the feature flag is off, or stores where
 * the per-store setting has not been enabled, the service exists as a no-op
 * — the constructor's subscription runs but never observes a store with
 * the setting, so no listener is registered and no per-event work happens.
 * This matches the existing iframe-render gating in `app.component.html`
 * so the bridge and the widget come up and tear down together.
 */
@Injectable({ providedIn: 'root' })
export class AiWidgetBridgeService implements OnDestroy {
  private listenerAttached = false;
  private readonly destroy$ = new Subject<void>();

  /** Most recently observed selected store; refreshed by the gating subscription. */
  private currentStore: ClientStore | null = null;
  /** Most recently observed cart UUID; refreshed by the cart subscription. */
  private currentCartUuid: string | null = null;

  constructor(
    private readonly store: Store,
    private readonly inactivityService: InactivityService,
    private readonly ngZone: NgZone,
  ) {
    // Gate: attach the listener only when a store with ORDERS_AI_ENABLED is
    // selected. The subscription also doubles as the source for
    // `currentStore`, which the message handler reads at event time.
    this.store
      .select(getSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedStore: ClientStore | null) => {
        this.currentStore = selectedStore;
      });

    this.store
      .select(getSelectedStore)
      .pipe(
        map((s: ClientStore | null) => !!s?.settings?.ORDERS_AI_ENABLED),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((shouldListen) => {
        if (shouldListen) {
          this.attachListener();
        } else {
          this.detachListener();
        }
      });

    this.store
      .select(getCurrentCartState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cartState) => {
        this.currentCartUuid = cartState?.uuid ?? null;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detachListener();
  }

  private attachListener(): void {
    if (this.listenerAttached) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('message', this.onMessage);
    this.listenerAttached = true;
  }

  private detachListener(): void {
    if (!this.listenerAttached) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('message', this.onMessage);
    this.listenerAttached = false;
  }

  /**
   * Validate-and-route a postMessage from the widget.
   *
   * Arrow function so `this` is preserved when used as an event listener.
   */
  private readonly onMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (!this.isAiOrderEnvelope(data)) {
      return;
    }

    // `event.origin` is intentionally NOT whitelisted here. The widget origin
    // varies per environment (`order-ai.testgodev.nl`,
    // `order-ai.wannaorder.net`, `order-ai.gonnaorder.com`) and per
    // white-label domain, and there is no stable allow-list we can ship. The
    // `storeId` equality check below is the trust anchor: the payload is
    // accepted only when it claims the same storeId the host is currently
    // displaying, which a third-party page cannot know.
    const selectedStore = this.currentStore;
    if (!selectedStore || selectedStore.id !== data.storeId) {
      return;
    }

    // Run the dispatch inside NgZone — `window.postMessage` events can fire
    // outside Angular's zone depending on the browser / Zone.js patching
    // state, and dispatching outside the zone would defer change detection
    // until the next zone-tracked task.
    this.ngZone.run(() => this.handleMessage(data, selectedStore));
  };

  private handleMessage(
    msg: AiOrderEnvelope,
    selectedStore: ClientStore,
  ): void {
    switch (msg.type) {
      case 'AI_ORDER_DRAFT_UPDATED':
        this.onDraftUpdated(msg, selectedStore);
        return;
      case 'AI_ORDER_USER_ACTIVE':
        this.inactivityService.notifyExternalActivity();
        return;
    }
  }

  private onDraftUpdated(
    msg: AiOrderDraftUpdatedMessage,
    selectedStore: ClientStore,
  ): void {
    const cartUuid = this.currentCartUuid;
    if (!cartUuid) {
      return;
    }
    if (msg.orderId && msg.orderId !== cartUuid) {
      // Defensive: the widget is the consumer of the host-injected cart
      // UUID, never the source of truth. A mismatch indicates the widget
      // was re-seeded with stale config; skip the reload and let the next
      // host-driven URL regeneration reconcile.
      return;
    }
    this.store.dispatch(
      new CheckExistingOrder(selectedStore.id, cartUuid, 'CHECKEXISTING'),
    );
  }

  private isAiOrderEnvelope(data: unknown): data is AiOrderEnvelope {
    if (!data || typeof data !== 'object') {
      return false;
    }
    const obj = data as Record<string, unknown>;
    if (typeof obj.storeId !== 'number') {
      return false;
    }
    if (obj.type === 'AI_ORDER_DRAFT_UPDATED') {
      return obj.orderId === null || typeof obj.orderId === 'string';
    }
    if (obj.type === 'AI_ORDER_USER_ACTIVE') {
      return obj.source === 'text' || obj.source === 'voice';
    }
    return false;
  }
}
